import { NumberGrid } from '@Animation/mechanics/base/number-grid'
import { NumberSelector } from '@Animation/mechanics/base/number-selector'
import { RED_BALL_ATLAS_FRAMES, RED_BALL_TEXTURE_KEYS } from './constants'
import { tweenPromise } from '@Animation/utils'
import { KenoAnimationScene } from '@Animation/scenes/keno-animation'

export class RedBallNumberSelector extends NumberSelector {
  ball: Phaser.GameObjects.Image

  startHeightScale = 0.4
  apexHeightScale = 0.75
  landingHeightScale = 0.15

  constructor(
    scene: Phaser.Scene,
    numberGrid: NumberGrid,
    miniGameSelections: number[],
    preNumberSelected: (index: number) => Promise<void>,
    onNumberSelected: (number: number) => void,
  ) {
    super(scene, numberGrid, miniGameSelections, preNumberSelected, onNumberSelected)

    this.ball = scene.add.image(0, 0, RED_BALL_TEXTURE_KEYS.ATLAS, RED_BALL_ATLAS_FRAMES.BALL)
    this.ball.setOrigin(0.5, 0.5)
    this.ball.setVisible(false)

    this.add(this.ball)
  }

  async skip(numbers: number[]) {
    super.skip(numbers)
    this.ball.setVisible(false)
  }

  async targetSingleNumber(number: number): Promise<void> {
    const randomStartX = this.getRandomPosition().x
    const targetX = this.numberGrid.x + this.numberGrid.numbers[number - 1].x
    const targetY = this.numberGrid.y + this.numberGrid.numbers[number - 1].y

    const startY = 600 + (600 - targetY)

    const deltaX = targetX - randomStartX
    const deltaY = targetY - startY

    let finalX = targetX + deltaX
    let finalY = targetY + deltaY

    const minY = -500
    const minX = -500
    const maxX = 1000

    const landTime = 1000
    const bounceDuration = 1000

    //Guarantee Ball will bounce off screen
    while (finalX >= minX && finalX <= maxX && finalY >= minY) {
      finalX += deltaX
      finalY += deltaY
    }

    this.ball.setX(randomStartX)
    this.ball.setY(startY)
    this.ball.setVisible(true)
    this.ball.setScale(this.landingHeightScale)

    const yPositionTween = tweenPromise(this.scene, {
      targets: this.ball,
      duration: landTime,
      y: targetY,
    })

    const xPositionTween1 = tweenPromise(this.scene, {
      targets: this.ball,
      duration: landTime,
      x: targetX,
      ease: Phaser.Math.Easing.Back.In,
    })

    const scaleTween1 = tweenPromise(this.scene, {
      targets: this.ball,
      duration: landTime / 2,
      scale: this.apexHeightScale,
      ease: Phaser.Math.Easing.Circular.Out,
    })

    const scaleTween2 = tweenPromise(this.scene, {
      targets: this.ball,
      duration: landTime / 2,
      scale: this.landingHeightScale,
      ease: Phaser.Math.Easing.Circular.In,
    })

    yPositionTween()
    xPositionTween1()
    await scaleTween1()
    await scaleTween2()

    this.numberGrid.selectNumber(number)

    const bounceTweenY = tweenPromise(this.scene, {
      targets: this.ball,
      duration: bounceDuration,
      y: finalY,
    })

    const bounceTweenX = tweenPromise(this.scene, {
      targets: this.ball,
      duration: bounceDuration,
      x: finalX,
    })

    const bounceTweenScale = tweenPromise(this.scene, {
      targets: this.ball,
      duration: bounceDuration,
      scale: this.startHeightScale,
      ease: Phaser.Math.Easing.Circular.Out,
    })

    await Promise.all([bounceTweenX(), bounceTweenY(), bounceTweenScale()])
    this.ball.setVisible(false)
  }

  async targetNumbers(numbers: number[]): Promise<void> {
    const kenoScene = this.scene as KenoAnimationScene
    kenoScene.numberSelector?.setDepth(60)
    await super.targetNumbers(numbers)
    kenoScene.numberSelector?.setDepth(20)
  }
}
